import { useEffect, useState } from "react";
import { Link, useParams } from "react-router";

import { type ClaimRow, anchorText, getClaim, message } from "./claimsClient";

/** One claim: its Context Boundary, evidence counts, result kind, and exact source regions. */
export function ClaimPage() {
  const { claimId = "" } = useParams();
  const [claim, setClaim] = useState<ClaimRow | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let current = true;
    getClaim(claimId)
      .then((found) => {
        if (current) {
          setClaim(found);
          setError(null);
        }
      })
      .catch((caught: unknown) => {
        if (current) {
          setError(message(caught));
        }
      });
    return () => {
      current = false;
    };
  }, [claimId]);

  if (error) {
    return <p role="alert">Could not load: {error}</p>;
  }
  if (!claim) {
    return <p>Loading claim.</p>;
  }
  const evidence = claim.evidence;
  return (
    <section aria-labelledby="claim-title">
      <p>
        <Link to="/claims">All claims</Link>
      </p>
      <h1 id="claim-title">{claim.statement}</h1>
      <dl aria-label="Claim">
        <dt>Lane</dt>
        <dd>{claim.lane}</dd>
        <dt>Type</dt>
        <dd>{claim.claim_type}</dd>
        <dt>Status</dt>
        <dd>{claim.status}</dd>
        <dt>Result</dt>
        <dd>{claim.output_kind ? `${claim.output_kind} (${claim.result_form})` : "None yet"}</dd>
        <dt>Target</dt>
        <dd>
          {claim.target_object
            ? [claim.target_object, claim.target_attribute].filter(Boolean).join(".")
            : "None"}
        </dd>
      </dl>
      <h2>Context Boundary</h2>
      {claim.boundary ? (
        <dl aria-label="Context Boundary">
          {Object.entries(claim.boundary).map(([dimension, value]) => (
            <div key={dimension}>
              <dt>{dimension}</dt>
              <dd>{JSON.stringify(value)}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <p role="status">This claim has no recorded Context Boundary.</p>
      )}
      <h2>Evidence</h2>
      {evidence ? (
        <p>
          {evidence.items} items, {evidence.admitted_supporting_channels} admitted supporting
          channels, {evidence.admitted_contrary_channels} admitted contrary channels,{" "}
          {evidence.contradictions} contradictions.
        </p>
      ) : (
        <p>No sealed evidence package yet.</p>
      )}
      <h2>Source regions</h2>
      {claim.anchors.length === 0 ? (
        <p role="status">This claim cites no source region.</p>
      ) : (
        <ul aria-label="Source regions">
          {claim.anchors.map((anchor) => (
            <li key={`${anchor.source_region_id}-${anchor.observation_id ?? "region"}`}>
              {anchorText(anchor)} ({anchor.link_role}). Source version{" "}
              <code>{anchor.source_artifact_version_id}</code>, parsed document{" "}
              <code>{anchor.parsed_document_id}</code>, region <code>{anchor.source_region_id}</code>.{" "}
              <Link to="/source-explorer">Open the source explorer</Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
